import {
  ticketAttachmentsResponseSchema,
  ticketCommentsResponseSchema,
  ticketCommentSchema,
  ticketAttachmentSchema,
  ticketAiAssistResponseSchema,
  ticketDetailSchema,
  ticketHistoryResponseSchema,
  ticketListResponseSchema,
  ticketLookupsSchema,
  queueBoardSchema,
  managedQueueSchema,
  managedQueueListSchema,
  changeCalendarResponseSchema,
  okResponseSchema,
  type AssignTicketRequest,
  type BroadcastIncidentRequest,
  type ChangeCalendarQuery,
  type ChangeTicketStatusRequest,
  type CreateQueueRequest,
  type CreateTicketCommentRequest,
  type CreateTicketRequest,
  type LinkMajorIncidentChildRequest,
  type LinkProblemIncidentRequest,
  type ReorderQueuesRequest,
  type TicketAiSuggestionAction,
  type TicketAiAssistRequest,
  type TicketAiAssistResponse,
  type TicketListQuery,
  type UpdateChangeRequest,
  type UpdateIncidentRequest,
  type UpdateProblemRequest,
  type UpdateQueueRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch, ApiRequestError } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

export const ticketsQueryKey = ['tickets'] as const;
export const ticketLookupsQueryKey = ['tickets', 'lookups'] as const;
export const queueBoardQueryKey = ['tickets', 'queues', 'board'] as const;
export const inactiveQueuesQueryKey = ['tickets', 'queues', 'inactive'] as const;
export const changeCalendarQueryKey = ['tickets', 'changes', 'calendar'] as const;

export function ticketQueryKey(ref: string) {
  return ['tickets', ref] as const;
}

export function ticketHistoryQueryKey(ref: string) {
  return ['tickets', ref, 'history'] as const;
}

export function ticketCommentsQueryKey(ref: string) {
  return ['tickets', ref, 'comments'] as const;
}

export function ticketAiAssistQueryKey(ref: string) {
  return ['tickets', ref, 'ai-assist'] as const;
}

export function ticketAttachmentsQueryKey(ref: string) {
  return ['tickets', ref, 'attachments'] as const;
}

export function useTicketLookups() {
  return useQuery({
    queryKey: ticketLookupsQueryKey,
    queryFn: () => apiFetch('/tickets/lookups', ticketLookupsSchema),
  });
}

export function useTickets(query: TicketListQuery, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...ticketsQueryKey, query],
    enabled: options?.enabled ?? true,
    queryFn: () =>
      apiFetch(
        `/tickets${toSearchParams({
          page: query.page,
          pageSize: query.pageSize,
          sortBy: query.sortBy,
          sortOrder: query.sortOrder,
          q: query.q,
          status: query.status,
          priority: query.priority,
          type: query.type,
          categoryId: query.categoryId,
          queueId: query.queueId,
          assigneeId: query.assigneeId,
          unassigned: query.unassigned,
          createdFrom: query.createdFrom,
          createdTo: query.createdTo,
          isMajor: query.isMajor,
          problemLifecycle: query.problemLifecycle,
          changeWindow: query.changeWindow,
          changeRisk: query.changeRisk,
          changeApproval: query.changeApproval,
          slaRisk: query.slaRisk,
        })}`,
        ticketListResponseSchema,
      ),
  });
}

export function useQueueBoard() {
  return useQuery({
    queryKey: queueBoardQueryKey,
    queryFn: () => apiFetch('/tickets/queues/board', queueBoardSchema),
  });
}

export function useReorderQueues() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReorderQueuesRequest) =>
      apiFetch('/tickets/queues/reorder', queueBoardSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async (board) => {
      queryClient.setQueryData(queueBoardQueryKey, board);
      await queryClient.invalidateQueries({ queryKey: ticketLookupsQueryKey });
    },
  });
}

export function useAssignTicketQueue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, queueId }: { ticketId: string; queueId: string | null }) =>
      apiFetch(`/tickets/${ticketId}/assign`, ticketDetailSchema, {
        method: 'POST',
        body: JSON.stringify({ queueId }),
      }),
    onSuccess: async (ticket) => {
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useCreateQueue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateQueueRequest) =>
      apiFetch('/tickets/queues', managedQueueSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateQueueQueries(queryClient);
    },
  });
}

export function useUpdateQueue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateQueueRequest }) =>
      apiFetch(`/tickets/queues/${id}`, managedQueueSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateQueueQueries(queryClient);
    },
  });
}

export function useInactiveQueues(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: inactiveQueuesQueryKey,
    queryFn: () => apiFetch('/tickets/queues/inactive', managedQueueListSchema),
    enabled: options?.enabled ?? true,
  });
}

export function useDeleteQueue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/tickets/queues/${id}`, okResponseSchema, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await invalidateQueueQueries(queryClient);
    },
  });
}

function invalidateQueueQueries(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queueBoardQueryKey }),
    queryClient.invalidateQueries({ queryKey: ticketLookupsQueryKey }),
    queryClient.invalidateQueries({ queryKey: inactiveQueuesQueryKey }),
  ]);
}

export function useTicket(ref: string | undefined) {
  return useQuery({
    queryKey: ref ? ticketQueryKey(ref) : ['tickets', 'missing'],
    queryFn: () => apiFetch(`/tickets/${ref}`, ticketDetailSchema),
    enabled: Boolean(ref),
    refetchInterval: (query) => {
      const ticket = query.state.data;
      if (!ticket || ticket.ai.classified) {
        return false;
      }
      const ageMs = Date.now() - new Date(ticket.createdAt).getTime();
      return ageMs < 90_000 ? 2500 : false;
    },
  });
}

export function useTicketHistory(ref: string | undefined) {
  return useQuery({
    queryKey: ref ? ticketHistoryQueryKey(ref) : ['tickets', 'history', 'missing'],
    queryFn: () => apiFetch(`/tickets/${ref}/history`, ticketHistoryResponseSchema),
    enabled: Boolean(ref),
  });
}

export function useTicketComments(ref: string | undefined) {
  return useQuery({
    queryKey: ref ? ticketCommentsQueryKey(ref) : ['tickets', 'comments', 'missing'],
    queryFn: () => apiFetch(`/tickets/${ref}/comments`, ticketCommentsResponseSchema),
    enabled: Boolean(ref),
  });
}

export function useTicketAttachments(ref: string | undefined) {
  return useQuery({
    queryKey: ref ? ticketAttachmentsQueryKey(ref) : ['tickets', 'attachments', 'missing'],
    queryFn: () => apiFetch(`/tickets/${ref}/attachments`, ticketAttachmentsResponseSchema),
    enabled: Boolean(ref),
  });
}

function invalidateTicket(queryClient: ReturnType<typeof useQueryClient>, ref: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ticketsQueryKey }),
    queryClient.invalidateQueries({ queryKey: queueBoardQueryKey }),
    queryClient.invalidateQueries({ queryKey: changeCalendarQueryKey }),
    queryClient.invalidateQueries({ queryKey: ticketQueryKey(ref) }),
    queryClient.invalidateQueries({ queryKey: ticketHistoryQueryKey(ref) }),
    queryClient.invalidateQueries({ queryKey: ticketCommentsQueryKey(ref) }),
    queryClient.invalidateQueries({ queryKey: ticketAttachmentsQueryKey(ref) }),
    queryClient.invalidateQueries({ queryKey: ticketAiAssistQueryKey(ref) }),
  ]);
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTicketRequest) =>
      apiFetch('/tickets', ticketDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketsQueryKey });
      void queryClient.invalidateQueries({ queryKey: queueBoardQueryKey });
      void queryClient.invalidateQueries({ queryKey: changeCalendarQueryKey });
    },
  });
}

export function useAssignTicket(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignTicketRequest) =>
      apiFetch(`/tickets/${ref}/assign`, ticketDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketQueryKey(ref), ticket);
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useChangeTicketStatus(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ChangeTicketStatusRequest) =>
      apiFetch(`/tickets/${ref}/status`, ticketDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketQueryKey(ref), ticket);
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useAcceptTicketAiSuggestion(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TicketAiSuggestionAction) =>
      apiFetch(`/tickets/${ref}/ai-suggestion/accept`, ticketDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketQueryKey(ref), ticket);
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useRejectTicketAiSuggestion(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TicketAiSuggestionAction) =>
      apiFetch(`/tickets/${ref}/ai-suggestion/reject`, ticketDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketQueryKey(ref), ticket);
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useLatestTicketAiAssist(ref: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ticketAiAssistQueryKey(ref),
    enabled: options?.enabled ?? true,
    retry: false,
    queryFn: async (): Promise<TicketAiAssistResponse | null> => {
      try {
        return await apiFetch(`/tickets/${ref}/ai-assist/latest`, ticketAiAssistResponseSchema);
      } catch (error) {
        if (error instanceof ApiRequestError && error.error.statusCode === 404) {
          return null;
        }
        throw error;
      }
    },
  });
}

export function useRequestTicketAiAssist(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TicketAiAssistRequest) =>
      apiFetch(`/tickets/${ref}/ai-assist`, ticketAiAssistResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (run) => {
      queryClient.setQueryData(ticketAiAssistQueryKey(ref), run);
    },
  });
}

export function useUpdateIncident(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateIncidentRequest) =>
      apiFetch(`/tickets/${ref}/incident`, ticketDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketQueryKey(ref), ticket);
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useLinkMajorIncidentChild(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LinkMajorIncidentChildRequest) =>
      apiFetch(`/tickets/${ref}/incident/children`, ticketDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketQueryKey(ref), ticket);
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useUnlinkMajorIncidentChild(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (childId: string) =>
      apiFetch(`/tickets/${ref}/incident/children/${childId}`, ticketDetailSchema, {
        method: 'DELETE',
      }),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketQueryKey(ref), ticket);
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useBroadcastIncident(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BroadcastIncidentRequest) =>
      apiFetch(`/tickets/${ref}/incident/broadcast`, ticketDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketQueryKey(ref), ticket);
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useUpdateProblem(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProblemRequest) =>
      apiFetch(`/tickets/${ref}/problem`, ticketDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketQueryKey(ref), ticket);
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useLinkProblemIncident(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LinkProblemIncidentRequest) =>
      apiFetch(`/tickets/${ref}/problem/incidents`, ticketDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketQueryKey(ref), ticket);
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useUnlinkProblemIncident(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (incidentId: string) =>
      apiFetch(`/tickets/${ref}/problem/incidents/${incidentId}`, ticketDetailSchema, {
        method: 'DELETE',
      }),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketQueryKey(ref), ticket);
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useChangeCalendar(query: ChangeCalendarQuery) {
  return useQuery({
    queryKey: [...changeCalendarQueryKey, query],
    queryFn: () =>
      apiFetch(
        `/tickets/changes/calendar${toSearchParams({ year: query.year, month: query.month })}`,
        changeCalendarResponseSchema,
      ),
  });
}

export function useUpdateChange(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateChangeRequest) =>
      apiFetch(`/tickets/${ref}/change`, ticketDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketQueryKey(ref), ticket);
      await invalidateTicket(queryClient, String(ticket.number));
      await invalidateTicket(queryClient, ticket.id);
    },
  });
}

export function useAddTicketComment(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTicketCommentRequest) =>
      apiFetch(`/tickets/${ref}/comments`, ticketCommentSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void invalidateTicket(queryClient, ref);
    },
  });
}

export function useUploadTicketAttachment(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return apiFetch(`/tickets/${ref}/attachments`, ticketAttachmentSchema, {
        method: 'POST',
        body: form,
      });
    },
    onSuccess: async () => {
      await invalidateTicket(queryClient, ref);
    },
  });
}
